/**
 * Ce que l'écran des bons cadeaux lit et écrit.
 *
 * Le dessin du coupon vit dans `coupon.ts` et ne connaît pas la base ; ce
 * fichier est le seul point de contact avec Supabase.
 */

import { supabase } from '../supabase';
import { planche, type Bon } from './coupon';
import { MASCOTTE_PNG_BASE64 } from './mascotte';

export type EtatDuBon = 'disponible' | 'consomme' | 'perime' | 'a_venir';

export interface BonCadeau {
  id: string;
  code: string;
  label: string | null;
  origin: string;
  createdAt: string;
  validFrom: string | null;
  expiresAt: string | null;
  notes: string | null;
  issuedToUserId: string | null;
  issuedToName: string | null;
  issuedToEmail: string | null;
  periodLabels: string[];
  periodIds: string[];
  redeemedAt: string | null;
  redeemedCents: number | null;
  redeemedByName: string | null;
  etat: EtatDuBon;
}

/**
 * Une SAISON de stage : une identité, pas un intervalle.
 *
 * Elle ne porte plus ni année ni dates. Une période datée ne rattrapait pas
 * une semaine ajoutée hors de sa fenêtre — c'est le défaut du 5 septembre :
 * un bon d'automne était refusé sur une semaine d'automne. Ce qui empêche un
 * bon de servir à l'automne suivant est sa date de péremption, pas la période.
 */
export interface Periode {
  id: string;
  code: string;
  label: string;
}

/** Les origines telles que la contrainte `gratuity_origin_connue` les accepte. */
export const ORIGINES: { valeur: string; libelle: string }[] = [
  { valeur: 'abonnement_garderie', libelle: 'Abonnement garderie' },
  { valeur: 'tombola', libelle: "Tombola d'école" },
  { valeur: 'vip', libelle: 'VIP' },
  { valeur: 'geste_commercial', libelle: 'Geste commercial' },
  { valeur: 'autre', libelle: 'Autre' },
];

export const LIBELLE_ETAT: Record<EtatDuBon, string> = {
  disponible: 'Disponible',
  consomme: 'Utilisé',
  perime: 'Périmé',
  a_venir: 'Pas encore ouvert',
};

function enBonCadeau(l: Record<string, any>): BonCadeau {
  return {
    id: l.id, code: l.code, label: l.label ?? null, origin: l.origin,
    createdAt: l.created_at, validFrom: l.valid_from ?? null, expiresAt: l.expires_at ?? null,
    notes: l.notes ?? null,
    issuedToUserId: l.issued_to_user_id ?? null,
    issuedToName: l.issued_to_name ?? null,
    issuedToEmail: l.issued_to_email ?? null,
    periodLabels: l.period_labels ?? [],
    periodIds: l.period_ids ?? [],
    redeemedAt: l.redeemed_at ?? null,
    redeemedCents: l.redeemed_cents ?? null,
    redeemedByName: l.redeemed_by_name ?? null,
    etat: l.etat as EtatDuBon,
  };
}

/**
 * Les bons, du plus récent au plus ancien.
 *
 * PostgREST rend au plus 1 000 lignes : la pagination est explicite, et
 * `count` permet à l'écran d'annoncer le total plutôt que « au moins 1 000 ».
 */
export async function chargerBons(opts: {
  etat?: EtatDuBon | 'tous';
  recherche?: string;
  page?: number;
  parPage?: number;
} = {}): Promise<{ bons: BonCadeau[]; total: number }> {
  const parPage = opts.parPage ?? 50;
  const page = opts.page ?? 0;

  let q = supabase
    .from('v_gratuity_codes')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    /* Second critère : deux bons du même lot partagent la milliseconde. */
    .order('code', { ascending: true })
    .range(page * parPage, page * parPage + parPage - 1);

  if (opts.etat && opts.etat !== 'tous') q = q.eq('etat', opts.etat);
  if (opts.recherche?.trim()) {
    const t = opts.recherche.trim();
    q = q.or(`code.ilike.%${t}%,label.ilike.%${t}%`);
  }

  const { data, error, count } = await q;
  if (error) throw error;
  return { bons: (data ?? []).map(enBonCadeau), total: count ?? 0 };
}

export async function chargerPeriodes(): Promise<Periode[]> {
  /* Dans l'ordre de l'année scolaire, qui est celui dont une équipe parle. */
  const { data, error } = await supabase
    .from('stage_periods')
    .select('id, code, label')
    .order('ordre');
  if (error) throw error;
  return (data ?? []).map((p: any) => ({ id: p.id, code: p.code, label: p.label }));
}

export interface DemandeDeLot {
  nombre: number;
  origine: string;
  libelle: string | null;
  valableDu: string | null;
  valableAu: string | null;
  periodIds: string[];
  /** NULL = au porteur. Rempli = nominatif, non transmissible. */
  issuedToUserId: string | null;
  notes: string | null;
}

/**
 * Édite un lot. Tous créés ou aucun — c'est la fonction en base qui le
 * garantit : un lot à moitié créé serait impossible à rattraper, on ne saurait
 * pas lesquels ont déjà été imprimés.
 */
export async function editerLot(d: DemandeDeLot): Promise<{ id: string; code: string }[]> {
  const { data, error } = await supabase.rpc('create_gratuity_codes', {
    p_count: d.nombre,
    p_origin: d.origine,
    p_label: d.libelle,
    p_valid_from: d.valableDu,
    p_expires_at: d.valableAu,
    p_period_ids: d.periodIds,
    p_issued_to_user_id: d.issuedToUserId,
    p_notes: d.notes,
  });
  if (error) throw error;
  return (data ?? []) as { id: string; code: string }[];
}

/* --------------------------------------------------------------------------
   Le coupon
   -------------------------------------------------------------------------- */

const mascotte = (): Uint8Array => {
  const brut = atob(MASCOTTE_PNG_BASE64);
  const octets = new Uint8Array(brut.length);
  for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i);
  return octets;
};

function jour(iso: string | null): string | null {
  if (!iso) return null;
  const [a, m, j] = iso.slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
}

/**
 * Ce qui est imprimé sur le coupon, déduit du bon lui-même.
 *
 * La mention du bas dit ce que le système **applique** réellement. Les coupons
 * de mai 2026 portaient « *valable uniquement à Watermael-Boitsfort » : une
 * restriction par centre que rien en base ne fait respecter. Imprimer une
 * promesse que le système n'oppose pas serait pire que de se taire — un parent
 * la lit, un autre l'ignore, et le secrétariat tranche à la main.
 *
 * Les périodes, elles, sont bien opposées par `quote_order`.
 */
export function enCoupon(b: BonCadeau): Bon {
  const debut = jour(b.validFrom);
  const fin = jour(b.expiresAt);
  const validite = debut && fin
    ? `CODE VALABLE POUR TOUTE INSCRIPTION DU ${debut} AU ${fin}`
    : fin
      ? `CODE VALABLE POUR TOUTE INSCRIPTION JUSQU'AU ${fin}`
      : 'CODE VALABLE POUR TOUTE INSCRIPTION';

  const mention = b.periodLabels.length === 0
    ? '*valable sur toutes nos périodes de stage'
    : `*valable en ${b.periodLabels.join(' et ')}`;

  return { code: b.code, valeur: '1 SEMAINE DE PLAINE DE VACANCES', validite, mention };
}

/** La planche, fabriquée ici même : rien ne transite, rien ne se stocke. */
export async function planchePdf(bons: BonCadeau[]): Promise<Uint8Array> {
  return await planche(bons.map(enCoupon), mascotte());
}

export function telecharger(octets: Uint8Array, nom: string) {
  const blob = new Blob([octets], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  a.click();
  /* L'URL objet retient le blob en mémoire tant qu'on ne la révoque pas. */
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function enBase64(octets: Uint8Array): string {
  let binaire = '';
  /* Par tranches : `fromCharCode(...tableau)` déborde la pile au-delà de ~100 ko. */
  for (let i = 0; i < octets.length; i += 8192) {
    binaire += String.fromCharCode(...octets.subarray(i, i + 8192));
  }
  return btoa(binaire);
}

/**
 * Envoie UN bon par courriel, coupon en pièce jointe.
 *
 * Un courriel ne porte qu'un seul bon : en envoyer quatre donnerait quatre
 * semaines à la même famille.
 */
export async function envoyerBon(
  bon: BonCadeau, destinataire: string, message: string | null,
): Promise<void> {
  const pdf = await planche([enCoupon(bon)], mascotte());
  const { data, error } = await supabase.functions.invoke('send-gratuity-email', {
    body: {
      code_id: bon.id,
      email_to: destinataire.trim(),
      pdf_base64: enBase64(pdf),
      message: message?.trim() || undefined,
    },
  });
  if (error) {
    /*
      Les fonctions edge rendent leur message dans le corps, pas dans
      `error.message` — sans cette lecture, l'écran afficherait
      « Edge Function returned a non-2xx status code », ce qui n'aide personne.
    */
    let detail = error.message;
    try {
      const corps = await (error as any).context?.json?.();
      if (corps?.error) detail = corps.error;
    } catch { /* on garde le message générique */ }
    throw new Error(detail);
  }
  if ((data as any)?.error) throw new Error((data as any).error);
}
